import { retryCheckpoint } from "@highmarch/app";
import type { GearItem, LevelSim, StarMask } from "@highmarch/domain";
import { LevelPhase, runStars } from "@highmarch/domain";
import { IMPACT } from "../render/statusFx";
import { topTowers } from "../ui/damageReport";
import { defeatPanelHtml, defeatReport } from "../ui/defeatPanel";
import type { Readiness } from "../ui/hub/readiness";
import { EndReport, LevelEndChoice, endTag, showLevelEnd } from "../ui/screens";
import { ShareHost, canShareCard, renderShareCard, shareCard, shareCardModel, shareText } from "../ui/shareCard";
import type { LevelParts } from "./levelParts";
import { markLevelUi } from "./levelUiScope";

/** What the end of the run reaches beyond the level's parts: the game's hooks. */
export interface EndHooks {
  /** Banks the run's stars and returns the level's best before them, for NEW BEST. */
  stars(run: StarMask): StarMask;
  /** Tells the game the run is over; it returns the best before this run and, on a loss, the level's readiness. */
  runOver(reached: number, won: boolean): { previousBest: number; readiness: Readiness | null } | null;
  readonly openChest: (() => GearItem[]) | null;
  /** The player chose where to go from the end screen. */
  leave(level: number, won: boolean, choice: LevelEndChoice): void;
}

/**
 * The end of the run: waits for the last kills' rounds to land, then puts up the end screen with the stars, the
 * verdict, the report and the share card, and tells the game.
 */
export class LevelEnd {
  /** The longest the end screen waits for the last kills' rounds to land: every round lands (or times out) by then. */
  static readonly EndWaitCapSec = IMPACT.maxSeconds + IMPACT.graceSeconds;
  /** Real seconds the escape ribbon holds before the defeat screen when a rare took the fortress (M36). */
  static readonly RareEscapeHoldSec = 2.2;
  private isOver = false;
  private el: HTMLElement | null = null;
  /** Real seconds since the sim ended with rewards still in the air (the end screen waits for them, capped). */
  private overFor = 0;

  constructor(private readonly p: LevelParts, private readonly h: EndHooks) {}

  /** The run is over: the end screen is up or on its way. */
  get ended(): boolean { return this.isOver; }
  /** The end screen is on screen. */
  get shown(): boolean { return this.el !== null; }

  /**
   * The sim is over: the end screen comes up once the last kills' rounds have landed (the points pill then shows the
   * whole reward), or after EndWaitCapSec whatever happens. It used to come up to ~1 s before the last death played.
   */
  due(dt: number): boolean {
    this.overFor += dt;
    // M36: a rare that broke through has its moment first — the escape ribbon holds ~2 s before the defeat screen
    if (this.p.sim.rareRun?.outcome === "escaped" && this.overFor < LevelEnd.RareEscapeHoldSec) return false;
    if (endWaitOver(this.p.rewards.pending, this.overFor)) return true;
    this.p.hud.refresh(this.p.sim, this.p.rewards.shown(this.p.sim.points));
    return false;
  }

  show(): void {
    if (this.isOver) return;
    this.isOver = true;
    const p = this.p, h = this.h, sim = p.sim;
    p.scene.placement.clear();
    p.hand.refresh(sim);
    p.hud.refresh(sim, p.rewards.shown(sim.points));
    const won = sim.phase === LevelPhase.Won;
    const level = sim.levelNumber;
    p.hud.rotate.classList.add("hidden");
    p.hud.setControlsSuppressed(true);
    p.pause.clear();                                             // the end screen is not a pause: no layer, no duck, no wake lock
    p.hud.bossBar.hide();                                        // a boss bar mid-drain must not sit on the end screen
    p.guide.detach();                                            // the end screen is its own guide screen
    const run = runStars(sim, p.session.tally);
    const before = h.stars(run);
    const report = this.report(run);
    this.el = showLevelEnd(p.uiRoot, sim, won && !sim.isIron && !sim.isEndless && !sim.isDaily && !sim.isEventRun && !sim.isChallenge && level < sim.balance.levels.length, (choice) => h.leave(level, won, choice), h.openChest, { run, before }, this.runOver(won), endGuideFacts(won, sim.firstClear, level), report.report);
    markLevelUi(this.el);                                        // the hub sweeps it if the level's teardown never ran
    p.moments.stop();                                            // the last stand lets go: no heartbeat under the end screen
    p.wyrm.stop();                                               // M40: no Wyrm banner or choice over the end screen
    const verdict = p.moments.verdict(sim);
    if (verdict) endTag(this.el, verdict.title, verdict.kind);
    report.prepare();                                            // after the verdict: it ranks among the card's moments
  }

  /**
   * The end screen's report: the top towers by damage (R2) and the share card (U19). The card's picture is drawn once
   * the verdict is in (`prepare`), so the tap on SHARE reaches the share sheet at once, inside its own gesture.
   */
  private report(run: StarMask): { report: EndReport; prepare: () => void } {
    const sim = this.p.sim, towers = topTowers(sim, this.p.session.damageByTower);
    const host: ShareHost | null = typeof navigator !== "undefined" && canShareCard(navigator) ? navigator : null;
    let text = "", png: Blob | null = null;
    const share = host ? () => shareCard(host, text, png) : null;
    const prepare = () => {
      if (!host) return;
      const model = shareCardModel(sim, run, this.p.moments.top, towers);
      text = shareText(model);
      void renderShareCard(model).then((b) => { png = b; });
    };
    return { report: { towers, share }, prepare };
  }

  /** Tells the game the run is over and, on a loss, returns the defeat screen's lesson (defeatPanel.ts). */
  private runOver(won: boolean): string {
    const sim = this.p.sim, session = this.p.session;
    const reached = reachedWave(sim, won);
    const info = this.h.runOver(reached, won);
    if (won || !info) return "";
    const report = defeatReport(sim.balance, { reached, stages: sim.stages, previousBest: info.previousBest, leaksByType: session.leaksByType, owned: session.ownedTowers, readiness: info.readiness, checkpoint: retryCheckpoint(session)?.wave ?? 0,
      rareEscaped: sim.rareRun?.outcome === "escaped" ? sim.balance.enemies[sim.rareRun.archetype].name : null,
      ...((session.modifiers.supplies?.checkpoints ?? 0) > 0 ? { banner: { left: session.checkpointsAllowed - session.checkpointsUsed } } : {}) });
    return defeatPanelHtml(sim.balance, report);
  }

  dispose(): void { this.el?.remove(); }
}

/** The end screen waits no longer: every kill's reward has landed, or the cap is up. */
export function endWaitOver(pending: number, overFor: number): boolean {
  return pending === 0 || overFor >= LevelEnd.EndWaitCapSec;
}

/** The wave the run reached: every stage on a win, the stage it fell on (at least the first) on a loss. */
export function reachedWave(sim: Pick<LevelSim, "stages" | "stage">, won: boolean): number {
  return won ? sim.stages : Math.max(1, sim.stage);
}

/**
 * The end screen's facts for the guide. A first clear of level 1 is the save's first win; a loss on a level never
 * cleared is as near to "the first defeat" as the level can tell (end.firstLoss shows once per save either way).
 */
export function endGuideFacts(won: boolean, firstClear: boolean, level: number): { firstWin: boolean; firstLoss: boolean; highestCleared: number } {
  return { firstWin: won && firstClear && level === 1, firstLoss: !won && firstClear, highestCleared: won || !firstClear ? level : level - 1 };
}
